/**
 * @file geocode.js
 * @description Shared ZIP → coordinates lookup. A case with null lat/lng is
 *   invisible to the distance-based therapist marketplace, so case posting must
 *   never save one. Tries the third-party API directly first (fast), then falls
 *   back to our own `geocode-zip` edge function when the direct call fails —
 *   adblockers, privacy extensions, and corporate firewalls frequently block
 *   browser requests to unknown third-party hosts, which previously left some
 *   agencies' cases permanently ungeocoded. The edge function makes the lookup
 *   from Supabase's network, so the browser only has to reach our own domain.
 *   Single source of truth for both the posting form (live preview) and the
 *   post/update guards.
 * @module lib/geocode
 */
import { supabaseUrl } from "./supabase";

function isValidGeo(geo) {
  return geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng);
}

// Direct browser call to zippopotam.us — fast path for most users.
async function geocodeDirect(zip) {
  try {
    const res = await fetch(`https://api.zippopotam.us/us/${zip}`, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return null;
    const data = await res.json();
    const place = data?.places?.[0];
    if (!place) return null;
    const lat = parseFloat(place.latitude);
    const lng = parseFloat(place.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng, city: place["place name"], state: place["state abbreviation"] };
  } catch {
    return null;
  }
}

// Fallback through our own edge function — bypasses client-side blocking of the
// third-party host and adds a second provider server-side.
async function geocodeViaProxy(zip) {
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/geocode-zip?zip=${zip}`, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null;
    const data = await res.json();
    const lat = parseFloat(data?.lat);
    const lng = parseFloat(data?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng, city: data.city || "", state: data.state || "" };
  } catch {
    return null;
  }
}

/**
 * Resolve a 5-digit US ZIP to coordinates + city/state.
 * @param {string} zip
 * @returns {Promise<{lat:number,lng:number,city:string,state:string}|null>}
 *   null only when BOTH the direct call and the edge proxy fail.
 */
export async function geocodeZip(zip) {
  if (!/^\d{5}$/.test(String(zip || ""))) return null;
  const direct = await geocodeDirect(zip);
  if (isValidGeo(direct)) return direct;
  return await geocodeViaProxy(zip);
}
